import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import PushSubscriber from './components/PushSubscriber';
import { RootErrorBoundary } from './components/RootErrorBoundary';
import useServiceWorkerUpdater from './hooks/useServiceWorkerUpdater';
import WorkspaceWindows from './components/workspace/WorkspaceWindows';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';

// Admin
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AgendaStickerPage from './pages/AgendaStickerPage';
import CantieriContabilitaPage from './pages/admin/CantieriContabilitaPage';
import CronologiaPage from './pages/admin/CronologiaPage';
import RapportiniPage from './pages/admin/RapportiniPage';
import DocumentiPage from './pages/admin/DocumentiPage';
import OperaiPage from './pages/admin/OperaiPage';
import PreventiviPage from './pages/admin/PreventiviPage';
import ArchivioPage from './pages/admin/ArchivioPage';
import ScansioniPage from './pages/admin/ScansioniPage';
import FinanzePage from './pages/admin/FinanzePage';
import PresenzePage from './pages/admin/PresenzePage';
import FattureEntrataPage from './pages/admin/FattureEntrataPage';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import ClientiPage from './pages/admin/ClientiPage';
import BustePagaPage from './pages/admin/BustePagaPage';
import FattureUscitaPage from './pages/admin/FattureUscitaPage';
import ContiEmessiPage from './pages/admin/ContiEmessiPage';
import PermGuard from './components/PermGuard';
import GestioneAdminPage from './pages/admin/GestioneAdminPage';
import SociPage from './pages/admin/SociPage';
import BilancioPage from './pages/admin/BilancioPage';
import F24Page from './pages/admin/F24Page';
import BustePagaImportPage from './pages/admin/BustePagaImportPage';
import ImportRapportiniPage from './pages/admin/ImportRapportiniPage';
import AdminAccountsPage from './pages/admin/AdminAccountsPage';
import DiagnosticaErroriPage from './pages/admin/DiagnosticaErroriPage';
import MagazzinoPage from './pages/admin/MagazzinoPage';
import WorkerMagazzinoPage from './pages/worker/WorkerMagazzinoPage';
import AdminNotifichePage from './pages/admin/AdminNotifichePage';
import DDTPage from './pages/admin/DDTPage';
import PrimaNotaPage from './pages/admin/PrimaNotaPage';
import ScadenziarioPage from './pages/admin/ScadenziarioPage';
import PartitarioPage from './pages/admin/PartitarioPage';
import RegistriIvaPage from './pages/admin/RegistriIvaPage';
import CompanyHeaderPage from './pages/admin/CompanyHeaderPage';
import TemplateDocumentiPage from './pages/admin/TemplateDocumentiPage';
import MaterialiCatalogoPage from './pages/admin/MaterialiCatalogoPage';
import FornitoriPage from './pages/admin/FornitoriPage';
import FatturazionePrevistaPage from './pages/admin/FatturazionePrevistaPage';
import ResetContabilitaPage from './pages/admin/ResetContabilitaPage';
import ModalitaPagamentoPage from './pages/admin/ModalitaPagamentoPage';
import AppDesktopPage from './pages/admin/AppDesktopPage';

// Worker
import WorkerLayout from './layouts/WorkerLayout';
import WorkerDashboard from './pages/worker/WorkerDashboard';
import WorkerRapportiniList from './pages/worker/WorkerRapportiniList';
import WorkerRapportinoForm from './pages/worker/WorkerRapportinoForm';
import WorkerDocumenti from './pages/worker/WorkerDocumenti';
import WorkerNotifiche from './pages/worker/WorkerNotifiche';
import WorkerProfilo from './pages/worker/WorkerProfilo';
import WorkerScanner from './pages/worker/WorkerScanner';
import WorkerFerieMalattia from './pages/worker/WorkerFerieMalattia';

import './App.css';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // superadmin can access admin routes
    if (user.role === 'superadmin' && allowedRoles.includes('admin')) {
      return children;
    }
    // iter100h: operaio con flag `is_magazziniere` DEVE poter accedere a route
    // che consentono role="magazziniere" (equivalenza flag ↔ ruolo).
    if (user.is_magazziniere === true && allowedRoles.includes('magazziniere')) {
      return children;
    }
    // magazziniere: base come operaio (worker), ma ha accesso extra a /admin/magazzino.
    // Se prova a entrare su altre route admin, torna a /worker.
    if (user.role === 'magazziniere') {
      return <Navigate to="/worker" replace />;
    }
    return <Navigate to={user.role === 'admin' || user.role === 'superadmin' ? '/admin' : '/worker'} replace />;
  }
  
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'superadmin') return <Navigate to="/admin" replace />;
    if (user.role === 'magazziniere') return <Navigate to="/worker" replace />;
    return <Navigate to={user.role === 'admin' ? '/admin' : '/worker'} replace />;
  }
  
  return children;
}

// Stable role guards (avoid creating a new array on every render)
const ADMIN_ROLES = ['admin'];

// La vecchia sezione Contabilità è stata unificata in Cantieri (tab contabilita)
function RedirectContabilita() {
  const params = new URLSearchParams(window.location.search);
  params.set('tab', 'contabilita');
  return <Navigate to={`/admin/cantieri?${params.toString()}`} replace />;
}
const ADMIN_OR_SUPER = ['admin', 'superadmin'];
const WORKER_ROLES = ['operaio', 'magazziniere'];
const SUPER_ROLES = ['superadmin'];

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* iter103: sticker agenda per finestra always-on-top / PiP / secondo schermo */}
      <Route path="/agenda-sticker" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AgendaStickerPage />
        </ProtectedRoute>
      } />
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><AdminDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/cantieri" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><CantieriContabilitaPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/cronologia" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><CronologiaPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/rapportini" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="rapportini"><RapportiniPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/contabilita" element={<RedirectContabilita />} />
      <Route path="/admin/documenti" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="documenti"><DocumentiPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/operai" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><OperaiPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/preventivi" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="preventivi"><PreventiviPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/archivio" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><ArchivioPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/scansioni" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><ScansioniPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/finanze" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="situazione_finanze"><FinanzePage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/presenze" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="presenze"><PresenzePage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatture-entrata" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="fatture_ricevute"><PageErrorBoundary pageName="Fatture Ricevute"><FattureEntrataPage forceTipo="fattura" /></PageErrorBoundary></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/clienti" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="clienti"><ClientiPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/buste-paga" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="buste_paga"><BustePagaPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatture-uscita" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="fatture_emesse"><FattureUscitaPage forceTipo="fattura" /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/fatture-emesse" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="fatture_emesse"><FattureUscitaPage forceTipo="fattura" title="Fatture Emesse" subtitle="Ciclo attivo — fatture emesse ai clienti (FAT-A)" /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/conti-emessi" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="conti_emessi"><ContiEmessiPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/gestione-admin" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PermGuard section="gestione_admin"><GestioneAdminPage /></PermGuard></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/soci" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><SociPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/bilancio" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><BilancioPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/f24" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><F24Page /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/buste-paga-import" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><BustePagaImportPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/note-credito-emesse" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><FattureUscitaPage forceTipo="nota_credito" title="Note di Credito Emesse" subtitle="Ciclo attivo — note di credito emesse ai clienti (NCR-A)" /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/fatture-ricevute" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PageErrorBoundary pageName="Fatture Ricevute"><FattureEntrataPage forceTipo="fattura" title="Fatture Ricevute" subtitle="Ciclo passivo — fatture ricevute dai fornitori (FAT-P)" /></PageErrorBoundary></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/note-credito-ricevute" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PageErrorBoundary pageName="Note Credito Ricevute"><FattureEntrataPage forceTipo="nota_credito" title="Note di Credito Ricevute" subtitle="Ciclo passivo — note di credito ricevute dai fornitori (NCR-P)" /></PageErrorBoundary></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/scadenziario" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><ScadenziarioPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/partitario" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PartitarioPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/registri-iva" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><RegistriIvaPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/import-rapportini" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><ImportRapportiniPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/admin-accounts" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><AdminAccountsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/diagnostica-errori" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><DiagnosticaErroriPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/magazzino" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin', 'magazziniere']}>
          <AdminLayout><PageErrorBoundary pageName="Magazzino"><MagazzinoPage /></PageErrorBoundary></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/notifiche" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><AdminNotifichePage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/ddt" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PageErrorBoundary pageName="DDT"><DDTPage /></PageErrorBoundary></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/prima-nota" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PrimaNotaPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/materiali-catalogo" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><PageErrorBoundary pageName="Catalogo Materiali"><MaterialiCatalogoPage /></PageErrorBoundary></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fornitori" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><FornitoriPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fatturazione/fatturazione-prevista" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><FatturazionePrevistaPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/impostazioni/intestazione" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><CompanyHeaderPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/impostazioni/modelli-documenti" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><TemplateDocumentiPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/impostazioni/reset-contabilita" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><ResetContabilitaPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/impostazioni/modalita-pagamento" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><ModalitaPagamentoPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/impostazioni/app-desktop" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminLayout><AppDesktopPage /></AdminLayout>
        </ProtectedRoute>
      } />
      
      {/* Worker Routes */}      <Route path="/worker" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerDashboard /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/rapportini" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerRapportiniList /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/magazzino" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerMagazzinoPage /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/rapportini/nuovo" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerRapportinoForm /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/scanner" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerScanner /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/documenti" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerDocumenti /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/notifiche" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerNotifiche /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/profilo" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerProfilo /></WorkerLayout>
        </ProtectedRoute>
      } />
      <Route path="/worker/ferie-malattia" element={
        <ProtectedRoute allowedRoles={WORKER_ROLES}>
          <WorkerLayout><WorkerFerieMalattia /></WorkerLayout>
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Registra il Service Worker e ascolta gli aggiornamenti (mostra toast
  // "Nuova versione disponibile — Ricarica" quando il SW attiva un nuovo build).
  useServiceWorkerUpdater();
  // Aggressively remove the "Made with Emergent" badge that the platform injects at runtime
  useEffect(() => {
    const remove = () => {
      const candidates = document.querySelectorAll(
        '#emergent-badge, a[href*="emergent.sh"], a[href*="emergent.dev"]'
      );
      candidates.forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
    remove();
    const observer = new MutationObserver(remove);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    const interval = setInterval(remove, 500);
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // iter101: la cattura degli errori async non-Boundary (window.onerror / unhandledrejection)
  // è gestita UNA SOLA VOLTA da installGlobalCrashReporter() in index.js (con filtro rumore
  // benigno e dedup). Il vecchio handler duplicato qui causava doppio logging di ogni errore
  // → raggiungeva istantaneamente la soglia di alert e inviava email ad ogni apertura.

  return (
    <RootErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <WorkspaceWindows />
          <PushSubscriber />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </RootErrorBoundary>
  );
}

export default App;
